import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ModalCMS } from "components/modals/ModalCMS"
import { TopBar } from "components/navigation/TopBar"
import { Table } from "components/tables/Table"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { tenantOptions } from "constants/bonus"
import { enumCMSTypes, tableCMSColumns } from "constants/cms"
import { SortOrderBy, SortOrderType } from "constants/eums"
import { PERMISSIONS } from "constants/permissions"

import { CMSService } from "services/cms/cms.service"
import { CMSActionResponse, CreateCMSPayload } from "services/cms/type"

import { isMicroserviceTenant } from "config/featureFlags"

import { CMSColorEditor } from "../components/editors/CMSColorEditor"
import { CMSTenantConfigEditor } from "../components/editors/CMSTenantConfigEditor"
import { CMSTextEditor } from "../components/editors/CMSTextEditor"
import { FormValuesType } from "../components/modals/ModalCMS/ModalCMS"
import { SearchInput } from "../components/search/SearchInput"
import { prepareData } from "../util/api"


export type PayloadToCreateEditCMSPayload =
  | (FormValuesType & { type: enumCMSTypes })
  | (FormValuesType & {
      type: enumCMSTypes
      id: string
    })

export type ActiveStepType = {
  order?: number
  id: string
}

export const CMSPage = () => {
  const { t } = useTranslation(["translation"], { useSuspense: false })

  const [editRow, setEditRow] = useState<CreateCMSPayload | null>(null)
  const [searchValue, setSearchValue] = useState<string>("")
  const [openEditModal, setOpenEditModal] = useState<boolean>(false)
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<Date | number>(Date.now())
  const [currentTab, setCurrentTab] = useState<enumCMSTypes>(enumCMSTypes.HOMEPAGE_BANNER)
  const [tenant, setTenant] = useState<string>("")
  const [activeBanners, setActiveBanners] = useState<CMSActionResponse[]>([])
  const { user } = useProps()
  const { has } = usePermissions()

  const onEditRow = (row: any): void => {
    setEditRow(row)
    setOpenEditModal(true)
  }

  const editRowHandler = useCallback((): void => {
    setEditRow(null)
    setOpenEditModal(false)
  }, [])

  const createPromoCodeHandler = (): void => {
    setOpenCreateModal(false)
  }

  const onEditModalSubmit = useCallback(async ({ id, ...data }: PayloadToCreateEditCMSPayload): Promise<void> => {
    const updatedData = await prepareData(data)
    if (updatedData && id) {
      await CMSService.editCMS(id, updatedData)
      setTableUpdatedAt(Date.now())
    }
  }, [])

  const onDelete = useCallback(
    async (id: string | undefined) => {
      if (!id) return
      try {
        await CMSService.deleteCMS(id)
        editRowHandler()
        setTableUpdatedAt(Date.now())
      } catch (err: any) {
        if (err?.details?.reason === "BANNER_IN_USE") {
          toast.error(t("cms.errors.bannerInUse", { bonusName: err.details.bonusTitle }) as string)
        }
      }
    },
    [editRowHandler, t]
  )

  const onCreateModalSubmit = useCallback(async ({ ...data }: PayloadToCreateEditCMSPayload): Promise<void> => {
    const updatedData = await prepareData(data)
    if (updatedData) {
      await CMSService.createCMS(updatedData)
      setTableUpdatedAt(Date.now())
    }
  }, [])

  const fetchActiveCMSBanners = useCallback(async () => {
    const data = await CMSService.getActiveCMSBanners(editRow?.tenant_id, currentTab)

    if (data) {
      setActiveBanners(data)
    }
  }, [editRow, currentTab])

  useEffect(() => {
    fetchActiveCMSBanners()
  }, [tableUpdatedAt])

  const actions = useMemo(() => [{ name: t("common.edit"), handler: onEditRow }], [])
  const activeSteps = useMemo<ActiveStepType[]>(
    () =>
      activeBanners
        .filter((item) => item.type === enumCMSTypes.HOMEPAGE_BANNER)
        .map((item) => ({
          order: item.order,
          id: item.id
        })),
    [activeBanners]
  )

  const cmsTabLabels: Record<string, string> = {
    [enumCMSTypes.HOMEPAGE_BANNER]: t("cms.tabs.homepageBanner"),
    [enumCMSTypes.BONUS_BANNER]: t("cms.tabs.bonusBanner"),
    [enumCMSTypes.TERMS_CONTENT]: t("cms.tabs.termsContent"),
    [enumCMSTypes.TENANT_COLORS]: t("cms.tabs.tenantColors"),
    [enumCMSTypes.TENANTS_CONFIG]: t("cms.tabs.tenantsConfig")
  }

  // Promotions moved to the CMS Pages module: they are pages with their own
  // URLs and metadata, not banner artwork.
  const tabsForAdmin = Object.keys(enumCMSTypes).filter(tab => tab !== enumCMSTypes.PROMOTIONS)
  const tabsForOperators = tabsForAdmin.filter((tab) => tab !== enumCMSTypes.TENANTS_CONFIG && tab !== enumCMSTypes.TENANT_COLORS)

  const tabs = user && has(PERMISSIONS.PAGE_CMS_ADMIN_TABS) ? tabsForAdmin : tabsForOperators

  let defaultSortOrderBy: SortOrderBy | string = SortOrderBy.ID
  if (isMicroserviceTenant()) {
    defaultSortOrderBy = "name"
  }

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("pages.cms.title")} />
      <div className="tabs gap-2">
        {tabs.map((tab, index) => (
          <a
            key={index}
            onClick={() => setCurrentTab(tab as enumCMSTypes)}
            className={clsx(
              "flex cursor-pointer items-center text-left py-3 rounded-lg px-4 text-base border text-[rgba(6,7,21,1)] dark:text-white border-[rgba(132,_0,_168,_1)] dark:border-transparent transition-all dark:bg-[#0C0C1A] hover:border-[rgba(255,255,255,0.1)]",
              {
                "transition-all text-white bg-[rgba(132,_0,_168,_1)] dark:bg-[rgba(132,_0,_168,_1)]": currentTab === tab,
                "hover:border-[rgba(132,_0,_168,_0.8)] hover:dark:border-[rgba(132,_0,_168,_0.8)]  hover:bg-[rgba(169,78,206,0.2)] dark:hover:bg-[rgba(169,78,206,0.15)]":
                  currentTab !== tab
              }
            )}
          >
            {cmsTabLabels[tab] || tab}
          </a>
        ))}
      </div>

      {currentTab === enumCMSTypes.TENANTS_CONFIG ? (
        <CMSTenantConfigEditor />
      ) : currentTab === enumCMSTypes.TENANT_COLORS ? (
        <CMSColorEditor />
      ) : currentTab === enumCMSTypes.TERMS_CONTENT ? (
        <CMSTextEditor />
      ) : (
        <>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end justify-between">
            <div className="max-w-sm flex flex-col gap-2 sm:flex-row sm:gap-8">
              <SearchInput placeholder={t("form.search")} onChange={setSearchValue} />
              <DropdownMenu
                placeholder={t("form.allTenants")}
                options={tenantOptions}
                value={tenant}
                onChange={(ev: ChangeEvent<HTMLSelectElement>) => setTenant(ev.target.value)}
                rounded
              />
            </div>

            <button
              className="btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
              onClick={() => setOpenCreateModal(true)}
            >
              {t("common.create")}
            </button>
          </div>

          <Table
            title={t("cms.banners")}
            handler={CMSService.getCMSTable}
            columns={tableCMSColumns}
            actions={actions}
            updatedAt={tableUpdatedAt}
            filters={{ q: searchValue, type: currentTab }}
            defaultSortOrder={SortOrderType.ASC}
            defaultSortOrderBy={defaultSortOrderBy}
            onRowClick={(row: CreateCMSPayload) => onEditRow(row)}
          />
        </>
      )}

      <ModalCMS
        open={openEditModal}
        data={editRow}
        handler={editRowHandler}
        onSubmit={onEditModalSubmit}
        type={currentTab}
        onDelete={onDelete}
        activeSteps={activeSteps}
      />
      <ModalCMS
        open={openCreateModal}
        handler={createPromoCodeHandler}
        onSubmit={onCreateModalSubmit}
        type={currentTab}
        activeSteps={activeSteps}
      />
    </div>
  )
}
